import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  styled,
  Typography,
  useTheme,
  Stack,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Menu,
  Popover,
  Link,
} from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  HistoryEduOutlined,
  InfoOutlined,
  MoreVert,
  Refresh,
  WarningAmberOutlined,
} from "@mui/icons-material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { API_SERVICE_BACKEND, API_SERVICE_CREDITS, HELP_URL } from "src/config";
import AddonModal from "src/sections/components/addonComponent/AddonModal";
import { getFeatureFlags } from "src/utils/featureFlags";
import { getSessionStorage } from "src/utils/storage";
import toast from "react-hot-toast";
import { useLocation } from "react-router";
import { WhiteLabelConfigurationContext } from "src/contexts/ClientConfiguration";
import CreditsLogsModal from "src/sections/components/userCreditsLogs/CreditsLogsModal";
import moment from "moment";
import HelperLink from "src/components/HelperLink";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor: theme.palette.mode === "light" ? "#1a90ff" : "#308fe8",
  },
}));

const Credits = ({ userEmail, userDetails, getAccessToken }) => {
  const [creditData, setCreditData] = useState({});
  const [subscriptionData, setSubscriptionData] = useState({});
  const [resetDate, setResetDate] = useState(null);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const [addonsData, setAddonsData] = useState(null);
  const [isOutboundCallEnabled, setIsOutboundCallEnabled] = useState(false);
  const [activeRecurringAddOns, setActiveRecurringAddOns] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [userDetail, setUserDetail] = useState("");
  const [userDetailLoader, setUserDetailLoader] = useState(true);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const [infoContent, setInfoContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState("");
  const [leadsImportUsage, setLeadsImportUsage] = useState(null);
  const [leadsImportLoading, setLeadsImportLoading] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const { state } = useLocation();


  useEffect(() => {
    if (!userDetails) return;
    const base = userDetails?.userSubscription?.subscriptions?.base;
    if (base?.planId === "7-day") {
      setCurrentSubscription("trial");
    } else if (base) {
      setCurrentSubscription(base.packageName || "No Active Subscription");
    } else {
      setCurrentSubscription("No Active Subscription");
    }
  }, [userDetails]);

  const getUserInfo = async (email) => {
    setUserDetailLoader(true);
    try {
      const res = await axios.get(
        `${API_SERVICE_BACKEND}/getuserdetail?email=${encodeURIComponent(email)}`
      );
      if (!res.data.status) return;
      setUserDetail(res.data.data);
      setUserDetailLoader(false);
      return res.data.data;
    } catch (err) {
      setUserDetailLoader(false);
      //console.log({ err });
      return;
    }
  };
  useEffect(() => {
    getUserInfo(getSessionStorage("userEmail"));
  }, []);

  // Fetch credits
  const getCredits = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_SERVICE_CREDITS}/credits/?email=${encodeURIComponent(userEmail)}`
      );
      const data = response.data;
      setResetDate(data?.resetsAt);
      const processedData = {};
      data.credits.forEach((credit) => {
        processedData[credit.type.toLowerCase()] = credit;
      });
      setCreditData(processedData);
      setSubscriptionData(data.subscription);
    } catch (error) {
      console.error("Error fetching credit data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch leads import usage
  const getLeadsImportUsage = async () => {
    setLeadsImportLoading(true);
    try {
      const res = await axios.get(
        `${API_SERVICE_BACKEND}/leads-imports-usage/${userEmail}`
      );
      setLeadsImportUsage(res.data);
    } catch (error) {
      setLeadsImportUsage({
        error: error?.response?.data?.error || "Failed to fetch usage",
      });
    } finally {
      setLeadsImportLoading(false);
    }
  };

  useEffect(() => {
    if (userEmail) {
      getCredits();
      getLeadsImportUsage();
    }
    // eslint-disable-next-line
  }, [userEmail]);

  useEffect(() => {
    if (userDetail?.userSubscription?.subscriptions?.addOns?.recurring?.[1]) {
      const active =
        userDetail.userSubscription.subscriptions.addOns.recurring?.[1].filter(
          (sub) => sub.status !== "cancelled"
        );
      setActiveRecurringAddOns(active);
    } else {
      setActiveRecurringAddOns([]);
    }
  }, [userDetail]);

  useEffect(() => {
    axios
      .get(`${API_SERVICE_BACKEND}/addons`)
      .then((response) => {
        setAddonsData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching addons:", error);
      });
  }, []);

  useEffect(() => {
    const features = getFeatureFlags();
    setIsOutboundCallEnabled(features?.AI_VOICE_AGENT || false);
  }, []);

  const handleCancelAddon = async () => {
    setIsLoading(true);
    if (!userEmail) {
      setIsLoading(false);
      console.error("No user email found in session storage.");
      return;
    }
    try {
      await axios.post(`${API_SERVICE_BACKEND}/addon-subscription`, {
        email: userEmail,
        quantity: 0,
        addOnType: selectedAddon.planId,
      });
      setIsLoading(false);
      await getUserInfo(userEmail);
      toast.success("Subscription cancelled and data updated.");
    } catch (error) {
      console.error("Cancel failed ❌:", error);
      toast.error(
        error?.response?.data?.error || "Failed to cancel subscription."
      );
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      setOpenCancelConfirm(false);
    }
  };

  const getValue = (total, remaining) => {
    if (!total) return 0;
    return ((total - remaining) / total) * 100;
  };

  const whiteLabelCreditsMap = [
    { name: "AI", type: "ai.gp" },
    { name: "LEADS", type: "contact.email.lookup" },
    { name: "EMAIL VALIDATION", type: "contact.email.validate" },
    { name: "LEADS IMPORT", type: "leads.import" }, // <-- Added
    { name: "AI VOICE AGENT MINUTES", type: "ai.voice.minutes" },
    { name: "FORWARD CALL MINUTES", type: "phone.numbers.minutes" },
  ];

  const creditsMap = [
    { name: "AI", type: "ai.gp" },
    { name: "LEADS", type: "contact.email.lookup" },
    { name: "EMAIL VALIDATION", type: "contact.email.validate" },
    { name: "LEADS IMPORT", type: "leads.import" }, // <-- Added
    { name: "AI VOICE AGENT MINUTES", type: "ai.voice.minutes" },
    { name: "FORWARD CALL MINUTES", type: "phone.numbers.minutes" },
  ];

  const subscriptionDetails = {
    startDate: userDetails?.userSubscription?.subscriptions?.base?.startDate
      ? new Date(
        userDetails?.userSubscription?.subscriptions?.base?.startDate
      )?.toDateString()
      : null,
    endDate: userDetails?.userSubscription?.subscriptions?.base?.endDate
      ? new Date(
        userDetails?.userSubscription?.subscriptions?.base?.endDate
      )?.toDateString()
      : null,
    resetsAt: resetDate ? new Date(resetDate)?.toDateString() : null,
    isFreeSubscription:
      userDetails?.userSubscription?.subscriptions?.base?.planId === "free",
  };

  const hasActiveSubscription =
    currentSubscription && currentSubscription !== "No Active Subscription";

  const handleManageSubscription = async () => {
    try {
      const baseURL = `${API_SERVICE_BACKEND}/createCustomerPortalSession?userEmail=${encodeURIComponent(sessionStorage.getItem(
        "userEmail"
      ))}`;
      const response = await axios.get(baseURL);
      if (response?.data?.urls?.length > 0) {
        for (const url of response.data.urls) {
          window.open(url, "_blank");
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.message);
    }
  };
  const { subUserStatus } = useContext(WhiteLabelConfigurationContext);

  return (
    <Stack gap={2}>
      <Stack
        direction={"row"}
        gap={2}
        justifyContent={"space-between"}
        alignItems={"flex-start"}
        sx={{
          flexWrap: "wrap",
        }}
      >
        <Stack
          sx={{
            width: userDetails?.whiteLabelUserId
              ? "100%"
              : { xs: "100%", md: "49%" },
            
            
          }}
          spacing={2}
        >
          <Card
            sx={{
              //marginTop: "10px",
              borderRadius: "8px",
              border: "1px solid #EAECF0",
            }}
            className="glass-wrapper"
          >
            <CardContent>
              <Typography
                className="subHeadding"
                sx={{
                  fontWeight: "700",
                  fontSize: "16px",
                  color: theme.palette.text.primary,
                }}
              >
                Current Subscription
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: theme.palette.text.secondary,
                }}
              >
                Overview and details of your current subscription plan.
              </Typography>
              <div style={{ marginTop: "8px" }}>
                <Stack direction="column" spacing={2} sx={{ fontSize: "14px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      height: "48px",
                      px: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      backgroundColor: "background.paper",
                      boxShadow: 1,
                      fontSize: "14px",
                    }}
                    className="glass-input"
                  >
                    <Typography
                      sx={{ color: "text.primary", userSelect: "none" }}
                    >
                      {currentSubscription === "trial"
                        ? "7 Days Free Trial"
                        : currentSubscription || "No Active Subscription"}
                    </Typography>
                    {!(userDetail?.whiteLabelUserId && subUserStatus) && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleManageSubscription}
                        sx={{
                          height: "36px",
                          minWidth: "60px",
                          padding: "0 12px",
                        }}
                        disabled={userDetails?.hasTeamMemberLimitedAccess || userDetails?.isVIPUser}
                      >
                        Manage Subscription
                      </Button>
                    )}
                  </Box>
                </Stack>
              </div>
              {hasActiveSubscription && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    marginBottom: 1,
                    width: "100%"
                  }}
                >
                  {loading ? (
                    <Stack direction="row" spacing={2} mt={2} width={"100%"} justifyContent={"space-between"}>
                      <Skeleton variant="text" width={120} height={30} />
                      <Skeleton variant="text" width={120} height={30} />
                      <Skeleton variant="text" width={120} height={30} />
                    </Stack>
                  ) : (
                    <Box sx={{ p: 0, overflow: "hidden", width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          mt: 3,
                          gap: 1.5
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            gap: 0.5
                          }}
                        >
                          <CalendarTodayIcon
                            sx={{
                              fontSize: 18,
                              color: theme.palette.primary.main,
                              mr: 0.5,
                              mt: "-3px"
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: theme.palette.text.secondary,
                            }}
                          >
                            Purchased on:{" "}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                              ml: "4px !important"
                            }}
                          >
                            {subscriptionDetails?.startDate ? (
                              moment(new Date(subscriptionDetails.startDate)).format("MMM DD, YYYY")
                            ) : (
                              "Not available"
                            )}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            gap: 0.3
                          }}
                        >
                          <AutorenewIcon
                            sx={{
                              fontSize: 20,
                              color: theme.palette.primary.main,
                              mr: 0.5,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: theme.palette.text.secondary,
                              mr: 0.5,
                            }}
                          >
                            Resets on:{" "}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                              ml: "4px !important"
                            }}
                          >
                            {subscriptionDetails?.resetsAt ? (
                              moment(new Date(subscriptionDetails.resetsAt)).format("MMM DD, YYYY")
                            ) : (
                              "Does not reset"
                            )}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            gap: 0.5
                          }}
                        >
                          <AccessTimeIcon
                            sx={{
                              fontSize: 18,
                              color: theme.palette.primary.main,
                              mt: "-1px"
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: theme.palette.text.secondary,
                              mr: 0.5,
                            }}
                          >
                            Expires on:{" "}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                              ml: "4px !important"
                            }}
                          >
                            {subscriptionDetails?.endDate ? (
                              moment(new Date(subscriptionDetails.endDate)).format("MMM DD, YYYY")
                            ) : (
                              "No expiry (unlimited)"
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
          <Card
            sx={{
              border: "1px solid #EAECF0",
              borderRadius:"8px",
            }}
            className="glass-wrapper"
          >
            <CardContent>
              {loading ? (
                <Skeleton variant="text" width="30%" height={32} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1, }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "700", color: theme.palette.text.primary, mb: 1 }}
                    >
                      Credits & Usage
                    </Typography>
                    {/* <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: (theme) => theme.palette.text.secondary,
                        lineHeight: "18px",
                        display: "inline",
                      }}
                    >
                      Set your usage limits and map to account features.{" "}
                      <Link
                        href={`${HELP_URL}/11928814-understanding-credits-and-usage`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          lineHeight: "18px",
                          color: (theme) => theme.palette.primary.main,
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                          display: "inline",
                        }}
                      >
                        [Learn More]
                      </Link>
                    </Typography> */}
                    <HelperLink
                      helperText={"Set your usage limits and map to account features. "}
                      actionText={"Learn more"}
                    />
                  </Box>


                  <Button
                    variant="outlined"
                    size="small"
                    className="glass-button-secondary-50"
                    startIcon={<HistoryEduOutlined />}
                    onClick={() => setLogsModalOpen(true)}

                  >
                    View Logs
                  </Button>
                </Box>

              )}
              {loading ? (
                <Stack spacing={2} mt={3}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={40}
                      animation="wave"
                    />
                  ))}
                </Stack>
              ) : (
                (userDetails.whiteLabelUserId
                  ? whiteLabelCreditsMap
                  : creditsMap
                ).map((credit) => {
                  if (credit.type === "leads.import") {
                    const hasLeadsImportFeature =
                      userDetails?.userSubscription?.subscriptions?.base
                        ?.features?.LEADS_IMPORT;

                    if (!hasLeadsImportFeature) {
                      return null;
                    }

                    const totalLeadsImport =
                      userDetails.userSubscription.subscriptions.base.features
                        .LEADS_IMPORT;
                    const usedLeadsImport =
                      leadsImportUsage?.totalLeadsImports || 0;
                    const thresholdValue = totalLeadsImport * 0.9;

                    return (
                      <Box
                        key={credit.name}
                        sx={{
                          marginX: "10px",
                          marginBottom: "20px",
                          marginTop: "10px !important",
                          color: theme.palette.text.primary,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                          }}
                        >
                          <Typography
                            component="p"
                            sx={{
                              fontWeight: "700",
                              fontSize: "16px",
                              color: theme.palette.text.primary,
                              marginTop: "10px",
                            }}
                          >
                            {leadsImportLoading ? (
                              <Skeleton width={120} />
                            ) : (
                              "LEADS IMPORT"
                            )}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            padding: "18px 20px 20px 18px",
                            border: "1px solid #EAECF0",
                            borderRadius: "10px",
                            gap: "14px",
                            color: theme.palette.text.primary,
                          }}
                          className="glass-wrapper-50"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: "500",
                                color: theme.palette.text.primary,
                              }}
                            >
                              {leadsImportLoading ? (
                                <Skeleton width={100} />
                              ) : leadsImportUsage &&
                                !leadsImportUsage.error ? (
                                `${usedLeadsImport.toLocaleString()} / ${totalLeadsImport.toLocaleString()}`
                              ) : leadsImportUsage && leadsImportUsage.error ? (
                                <span style={{ color: "red" }}>
                                  {leadsImportUsage.error}
                                </span>
                              ) : (
                                `0 / ${totalLeadsImport.toLocaleString()}`
                              )}
                            </Typography>
                            {Boolean(
                              !leadsImportLoading &&
                              leadsImportUsage &&
                              !leadsImportUsage.error &&
                              totalLeadsImport &&
                              totalLeadsImport > 0 &&
                              usedLeadsImport >= thresholdValue
                            ) && (
                                <Typography
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    gap: 0.5,
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    border: "1px solid red",
                                    borderRadius: 1,
                                    p: 1,
                                    color: "red",
                                  }}
                                  className="glass-chip-error"
                                >
                                  <WarningAmberOutlined
                                    sx={{ fontSize: "16px" }}
                                  />
                                  You are running low on credits
                                </Typography>
                              )}
                          </Box>
                          {leadsImportLoading ? (
                            <Skeleton
                              variant="rectangular"
                              width="100%"
                              height={8}
                            />
                          ) : (
                            <BorderLinearProgress
                              variant="determinate"
                              value={getValue(
                                totalLeadsImport,
                                totalLeadsImport - usedLeadsImport
                              )}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  }

                  // Default credits
                  const data = creditData[credit.type] || {
                    total: 0,
                    remaining: 0,
                    meta: { remaining: { db: 0 } },
                  };
                  const thresholdValue = data?.total * 0.9;

                  if (
                    credit.type === "ai.voice.minutes" &&
                    !isOutboundCallEnabled
                  ) {
                    return null;
                  }
                  return (
                    <Box
                      key={credit.name}
                      sx={{
                        marginX: "10px",
                        marginBottom: "20px",
                        marginTop: "10px !important",
                        color: theme.palette.text.primary,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "10px",
                        }}
                      >
                        <Typography
                          component="p"
                          sx={{
                            fontWeight: "700",
                            fontSize: "16px",
                            color: theme.palette.text.primary,
                            marginTop: "10px",
                          }}
                        >
                          {loading ? <Skeleton width={120} /> : credit.name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          padding: "18px 20px 20px 18px",
                          border: "1px solid #EAECF0",
                          borderRadius: "10px",
                          gap: "14px",
                          color: theme.palette.text.primary,
                        }}
                        className="glass-wrapper-50"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: theme.palette.text.primary,
                            }}
                          >
                            {loading ? (
                              <Skeleton width={60} />
                            ) : (
                              `${data?.total - data.meta?.remaining?.db || 0
                              } / ${data.total || 0}`
                            )}
                          </Typography>
                          {Boolean(
                            !loading &&
                            data?.total &&
                            data?.total > 0 &&
                            data?.consumed &&
                            data?.total - data.meta?.remaining?.db >=
                            thresholdValue
                          ) && (
                              <Typography
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  gap: 0.5,
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  border: "1px solid red",
                                  borderRadius: 1,
                                  p: 1,
                                  color: "red",
                                }}
                                className="glass-chip-error"
                              >
                                <WarningAmberOutlined sx={{ fontSize: "16px" }} />
                                You are running low on credits
                              </Typography>
                            )}
                        </Box>
                        {loading ? (
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={8}
                          />
                        ) : (
                          <BorderLinearProgress
                            variant="determinate"
                            value={getValue(
                              data.total,
                              data.meta?.remaining?.db || 0
                            )}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Stack>
        {!userDetails?.whiteLabelUserId && (
          <Card
            sx={{
              //marginTop: "10px",
              width: { xs: "100%", md: "49%" },
              borderRadius:"8px",
              border: "1px solid #EAECF0",
              alignSelf: "flex-start",
              height: "fit-content",
            }}
            className="glass-wrapper"
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 2,
                }}
              >
                <Box>
                  <Typography
                    // variant="h6"
                    className="subHeadding"
                    sx={{
                      fontWeight: "700",
                      fontSize:"16px",
                      color: theme.palette.text.primary,
                    }}
                  >
                    Add-Ons
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      // variant="body2"
                      sx={{  fontSize: "12px",color: theme.palette.text.secondary }}
                    >
                      Keep your add-on details up to date
                    </Typography>
                    <Refresh
                      sx={{
                        color: theme.palette.primary.main,
                        cursor: "pointer",
                      }}
                      onClick={() => getUserInfo(userEmail)}
                    />
                  </Box>
                </Box>
                <AddonModal
                  userDetails={userDetails}
                  userDetailsRecurring={activeRecurringAddOns}
                  addonsData={addonsData}
                  getUserInfo={getUserInfo}
                  getAccessToken={getAccessToken}
                  state={state}
                />
              </Box>
              {userDetailLoader ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : activeRecurringAddOns?.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: theme.palette.text.secondary,
                  }}
                >
                  No active subscriptions found.
                </Typography>
              ) : (
                activeRecurringAddOns
                  .filter((addon) => addon.planId !== "PHONE_NUMBERS_MINS")
                  .map((addon) => {
                    const isCredit = addon.type === "credits";
                    const isLeads = addon.planId === "LEADS";
                    const isEmailValidation = addon.planId === "EMAIL_VALIDATION";
                    const isAi = addon.planId === "AI";
                    const isAiKnowledgeBasePages = addon.planId === "AI_KNOWLEDGE_BASE_PAGES";
                    const isUserMailboxes = addon.planId === "USER_MAILBOXES";
                    const isCancelled = addon.cancelled;

                    let quantityDisplay = "";
                    if (isLeads) {
                      quantityDisplay = `${addon.quantity.toLocaleString()} leads`;
                    } else if (isEmailValidation) {
                      quantityDisplay = `${addon.quantity.toLocaleString()} validations`;
                    } else if (isAi) {
                      quantityDisplay = `${addon.quantity.toLocaleString()} credits`;
                    } else if (isAiKnowledgeBasePages) {
                      quantityDisplay = `${addon.quantity.toLocaleString()} pages`;
                    } else if (isUserMailboxes) {
                      quantityDisplay = `${addon.quantity.toLocaleString()} mailbox${addon.quantity > 1 ? "es" : ""}`;
                    } else {
                      quantityDisplay = isCredit
                        ? `${addon.quantity.toLocaleString()} minutes`
                        : `${addon.quantity.toLocaleString()} unit${addon.quantity > 1 ? "s" : ""
                        }`;
                    }

                    return (
                      <Box
                        key={addon.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "16px",
                          borderRadius: "10px",
                          marginBottom: 2,
                        }}
                        className="glass-wrapper-50"
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {addon.productName}
                            {addon.updatedSubscription && (
                              <IconButton
                                size="small"
                                sx={{ ml: 0.5, color: "red" }}
                                onClick={(e) => {
                                  setInfoAnchorEl(e.currentTarget);
                                  setInfoContent(addon.updatedSubscription);
                                }}
                              >
                                <InfoOutlined fontSize="small" />
                              </IconButton>
                            )}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {isCredit
                              ? "Credit-based add-on"
                              : "Feature-based add-on"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              mt: 0.5,
                            }}
                          >
                            Quantity: <strong>{quantityDisplay}</strong>
                          </Typography>
                          {addon.endDate && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                mt: 0.5,
                              }}
                            >
                              Expires on:{" "}
                              <strong>
                                {new Date(addon.endDate).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  }
                                )}
                              </strong>
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={isCancelled ? "Cancelled" : "Active"}
                            variant={isCancelled ? "outlined" : "filled"}
                            color={isCancelled ? "default" : "primary"}
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              paddingX: 1.5,
                              height: "28px",
                              borderRadius: "16px",
                              backgroundColor: isCancelled
                                ? "#f5f5f5"
                                : undefined,
                              color: isCancelled ? "black" : undefined,
                            }}
                          />
                          {!isCancelled && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(event) =>
                                  setAnchorEl(event.currentTarget)
                                }
                              >
                                <MoreVert />
                              </IconButton>
                              <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(null)}
                              >
                                <MenuItem
                                  onClick={() => {
                                    setSelectedAddon(addon);
                                    setOpenCancelConfirm(true);
                                    setAnchorEl(null);
                                  }}
                                >
                                  Cancel Subscription
                                </MenuItem>
                              </Menu>
                            </>
                          )}
                        </Stack>
                      </Box>
                    );
                  })
              )}
            </CardContent>
          </Card>
        )}

        {/* </Stack> */}
        <Dialog
          open={openCancelConfirm}
          onClose={() => setOpenCancelConfirm(false)}
        >
          <DialogTitle>Confirm Cancellation</DialogTitle>
          <DialogContent>
            Are you sure you want to cancel this subscription?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCancelConfirm(false)}>No</Button>
            <Button
              onClick={() => handleCancelAddon()}
              variant="contained"
              color="error"
            >
              {isLoading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Yes, Cancel
                </>
              ) : (
                "Yes, Cancel"
              )}
            </Button>
          </DialogActions>
        </Dialog>
        <Popover
          open={Boolean(infoAnchorEl)}
          anchorEl={infoAnchorEl}
          onClose={() => setInfoAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          {infoContent && (
            <Box sx={{ p: 2, maxWidth: 250 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Scheduled Update:
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                New Quantity:{" "}
                <strong>{infoContent.quantity.toLocaleString()}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Effective On:{" "}
                <strong>
                  {new Date(infoContent.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Status: <strong>{infoContent.status}</strong>
              </Typography>
            </Box>
          )}
        </Popover>
        <CreditsLogsModal
          open={logsModalOpen}
          onClose={() => setLogsModalOpen(false)}
        />
      </Stack>
    </Stack>
  );
};

export default Credits;
